import { APP_INITIALIZER, Inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';

import { DOCUMENT, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { WebView } from '@awesome-cordova-plugins/ionic-webview/ngx';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { OpenNativeSettings } from '@awesome-cordova-plugins/open-native-settings/ngx';
import { PhotoViewer } from '@awesome-cordova-plugins/photo-viewer/ngx';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { CoreModule } from 'src/app/core/core.module';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { FusionAuthService } from 'src/app/shared/services/fusion-auth.service';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FlexModule } from './pages/flex/flex.module';
import { GenericNotificationModalModule } from './shared/components/generic-notification/generic-notification.module';
import { ModalAccessibilityDirective } from './shared/directives/modal-accessibility.directive';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';
import { NativeHttpInterceptor } from './shared/interceptors/native-http.interceptor';
import { ServerUnavailableInterceptor } from './shared/interceptors/server-unavailable.interceptor';
import { StagingInterceptor } from './shared/interceptors/staging.interceptor';
import { TokenRefreshInterceptor } from './shared/interceptors/token-refresh.interceptor';
import { GraphqlModule } from './shared/modules/graphql.module';
import { Broadcaster, NgxCableService } from './shared/services/ngx-cable.service';
import { StorageService } from './shared/services/storage.service';

/**
 * Method to initialize module.
 *
 * @param authTokenService - Service to initialize the Auth tokens.
 * @param fusionAuthService - FusionAuthService.
 */
function initializeAuthTokens(
  authTokenService: AuthTokenService,
  fusionAuthService: FusionAuthService,
): () => Promise<void> {
  return async () => {
    const refreshToken = await authTokenService.refreshToken();

    // Get the access token if refresh token is present.
    if (refreshToken) {
      try {
        await fusionAuthService.handleTokenRefresh();
      } catch (error: unknown) {
        console.error('access token fetch during app load failed', error);
      }
    }
  };
}

@NgModule({
  declarations: [AppComponent, ModalAccessibilityDirective],
  imports: [
    BrowserModule,
    IonicModule.forRoot({ mode: 'md', rippleEffect: false, innerHTMLTemplatesEnabled: true }),
    HttpClientModule,
    FormsModule,
    IonicStorageModule.forRoot({
      driverOrder: [CordovaSQLiteDriver._driver, Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    ReactiveFormsModule,
    AppRoutingModule,
    GenericNotificationModalModule,
    GraphqlModule,
    FlexModule,
    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: true,
          strictActionSerializability: true,
        },
      },
    ),
    EffectsModule.forRoot(),
    environment.environmentName === 'production'
      ? []
      : StoreDevtoolsModule.instrument({
          maxAge: 25, // Retains last 25 states
          logOnly: false, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        }),
    CoreModule,
  ],
  providers: [
    StorageService,
    StatusBar,
    SplashScreen,
    Camera,
    Device,
    File,
    AppVersion,
    HTTP,
    FirebaseX,
    OpenNativeSettings,
    WebView,
    Keyboard,
    Deeplinks,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthTokens,
      deps: [AuthTokenService, FusionAuthService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StagingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenRefreshInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerUnavailableInterceptor,
      multi: true,
    },
    NgxCableService,
    Broadcaster,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      // NOTE: This interceptor must be provided last. If you are providing additional interceptors, place them above this interceptor.
      // This interceptor converts angular client http calls to native http calls. It must remain last.
      provide: HTTP_INTERCEPTORS,
      useClass: NativeHttpInterceptor,
      multi: true,
    },
    InAppBrowser,
    PhotoViewer,
    TitleCasePipe,
    Network,
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.recaptchaKey } as RecaptchaSettings,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(@Inject(DOCUMENT) private document: any) {
    this.loadSpreedlyJs();
  }

  /**
   * Set up spreedly js for payment.
   *
   * Reference - https://docs.spreedly.com/reference/express/v2/#express-javascript-api .
   */
  loadSpreedlyJs(): void {
    const script = document.createElement('script');
    script.src = 'https://core.spreedly.com/iframe/express-2.min.js';
    this.document.head.appendChild(script);
  }
}
